/**
 * "Æfing dagsins" — the quick menu of the practice on offer (api/practice useCurrentPractice: the
 * ongoing practice with songs, else today's, else the next within two weeks):
 *  - PracticeButton: in the song page's top bar and on Lög; opens the sheet. Nothing without a practice.
 *  - PracticeSheet: the practice's songs (ProgramList); a tap opens the song in the practice — its
 *    list tab, ‹ Fyrra · 3 / 12 · Næsta › and the list drawer then move between them. The song open
 *    now is marked. "Opna æfinguna" leads to the event page (where the conductor edits the songs).
 *  - PracticeHomeCard: the same on the home screen, with the first songs and "Opna fyrsta lag".
 * Works offline: the list is built from the cached calendar and lists until the server's arrives.
 */
import { CalendarCheck, ChevronRight, Play } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import type { EventDTO } from '../../shared/dto/events';
import { practiceListId } from '../../shared/program';
import { useServerNow } from '../api/calendar';
import { useCollection, useSongs } from '../api/library';
import { useCurrentPractice } from '../api/practice';
import { Button, Card, cx, Sheet, Spinner } from '../components/ui';
import { useI18n, useT, type MessageKey } from '../i18n';
import { capitalize, formatDay, formatTime, todayStr } from '../lib/dates';
import { orderedEntries } from '../viewer/setlist';
import { practiceSongHref } from './model';
import { ProgramList } from './ProgramList';

/**
 * `current`: the practice of the day — today's, or one already under way (also one that began
 * before midnight); else the next one. Text: "Í dag kl. 19:30" / "Fimmtudagur 23. október kl. 19:30".
 */
export function usePracticeWhen(ev: Pick<EventDTO, 'startsAt'>): { current: boolean; text: string } {
  const t = useT();
  const { loc } = useI18n();
  const now = useServerNow(60_000);
  const sameDay = todayStr(ev.startsAt) === todayStr(now);
  const time = formatTime(ev.startsAt, loc);
  return {
    current: sameDay || ev.startsAt <= now,
    text: sameDay ? t('practice.when_today', { time }) : t('practice.when_day', { day: capitalize(formatDay(ev.startsAt, loc), loc), time }),
  };
}

/** The practice's songs as this user gets them (server list; until then built on the device). */
export function usePracticeSongs(eventId: string) {
  const listQ = useCollection(practiceListId(eventId));
  const songsQ = useSongs();
  const songs = useMemo(() => new Map((songsQ.data ?? []).map((s) => [s.id, s])), [songsQ.data]);
  const list = listQ.data?.id === practiceListId(eventId) ? listQ.data : null;
  return { list, songs, loading: !list && listQ.fetchStatus === 'fetching' };
}

export function PracticeSheet({
  ev,
  open,
  onClose,
  currentEntryId,
}: {
  ev: EventDTO;
  open: boolean;
  onClose: () => void;
  currentEntryId?: string | null;
}) {
  const t = useT();
  const when = usePracticeWhen(ev);
  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={when.current ? t('practice.today') : t('practice.next')}
      footer={
        <Link
          to={`/events/${encodeURIComponent(ev.id)}`}
          onClick={onClose}
          className="flex min-h-12 w-full items-center justify-center gap-2 rounded-xl font-semibold text-[var(--accent)] ring-1 ring-[var(--line)] hover:bg-[var(--accent-soft)]"
        >
          {t('practice.open_event')}
          <ChevronRight className="size-5" aria-hidden />
        </Link>
      }
    >
      {open ? <PracticeSheetBody ev={ev} when={when.text} onClose={onClose} currentEntryId={currentEntryId ?? null} /> : null}
    </Sheet>
  );
}

function PracticeSheetBody({ ev, when, onClose, currentEntryId }: { ev: EventDTO; when: string; onClose: () => void; currentEntryId: string | null }) {
  const t = useT();
  const { list, songs, loading } = usePracticeSongs(ev.id);
  return (
    <div className="flex flex-col gap-4">
      <div>
        <p className="text-[19px] font-bold">{ev.title}</p>
        <p className="text-[16px] text-[var(--muted)]">{when}</p>
      </div>
      {list && list.entries.length > 0 ? (
        <ProgramList eventId={ev.id} list={list} songs={songs} currentEntryId={currentEntryId} onOpen={onClose} />
      ) : loading ? (
        <Spinner label={t('common.loading')} />
      ) : (
        <p className="rounded-2xl border border-dashed border-[var(--line)] px-4 py-6 text-center text-[var(--muted)]">{t('practice.empty')}</p>
      )}
    </div>
  );
}

/**
 * The quick-menu button; renders nothing without a practice on offer. `current`: the list the song
 * page shows (its entry is marked when it is this practice).
 */
export function PracticeButton({ current, className }: { current?: { collectionId: string; entryId: string | null } | null; className?: string }) {
  const ev = useCurrentPractice();
  return ev ? <PracticeButtonFor ev={ev} current={current ?? null} className={className} /> : null;
}

function PracticeButtonFor({ ev, current, className }: { ev: EventDTO; current: { collectionId: string; entryId: string | null } | null; className?: string }) {
  const t = useT();
  const when = usePracticeWhen(ev);
  const [open, setOpen] = useState(false);
  const inPractice = current?.collectionId === practiceListId(ev.id);
  const label: MessageKey = when.current ? 'practice.today' : 'practice.next';
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        aria-label={t('practice.button_aria', { title: ev.title, when: when.text })}
        title={`${t(label)} · ${when.text}`}
        className={cx(
          'relative inline-flex min-h-11 min-w-11 shrink-0 items-center justify-center gap-1.5 rounded-xl px-2 text-[15px] font-semibold transition sm:px-3',
          inPractice ? 'bg-[var(--accent)] text-[var(--accent-ink)]' : 'text-[var(--accent)] ring-1 ring-[var(--line)] hover:bg-[var(--accent-soft)]',
          className,
        )}
      >
        <CalendarCheck className="size-5" aria-hidden />
        <span className="hidden xl:inline">{t(label)}</span>
      </button>
      <PracticeSheet ev={ev} open={open} onClose={() => setOpen(false)} currentEntryId={inPractice ? current?.entryId : null} />
    </>
  );
}

/** Home screen: the practice on offer with its first songs; nothing without one. */
export function PracticeHomeCard() {
  const ev = useCurrentPractice();
  return ev ? <PracticeHomeCardFor ev={ev} /> : null;
}

const HOME_SONGS = 4;

function PracticeHomeCardFor({ ev }: { ev: EventDTO }) {
  const t = useT();
  const navigate = useNavigate();
  const when = usePracticeWhen(ev);
  const { list, songs, loading } = usePracticeSongs(ev.id);
  const [open, setOpen] = useState(false);
  const first = list ? orderedEntries(list.entries)[0] : undefined;
  const total = list?.entries.length ?? 0;
  return (
    <Card as="section" className="flex flex-col gap-3 p-5">
      <div className="flex items-start gap-3">
        <span className="flex size-11 shrink-0 items-center justify-center rounded-2xl bg-[var(--accent-soft)] text-[var(--accent)]">
          <CalendarCheck className="size-6" aria-hidden />
        </span>
        <div className="min-w-0 flex-1">
          <h2 className="text-[20px] leading-tight font-bold">{when.current ? t('practice.today') : t('practice.next')}</h2>
          <p className="truncate text-[15px] text-[var(--muted)]">
            {ev.title} · {when.text}
          </p>
        </div>
        {total > 0 ? <span className="shrink-0 pt-1 text-[14px] font-semibold text-[var(--muted)]">{t('practice.count', { count: total })}</span> : null}
      </div>
      {list && total > 0 ? (
        <ProgramList eventId={ev.id} list={list} songs={songs} limit={HOME_SONGS} />
      ) : loading ? (
        <Spinner label={t('common.loading')} />
      ) : (
        <p className="text-[var(--muted)]">{t('practice.empty')}</p>
      )}
      <div className="flex flex-wrap gap-2">
        {first ? (
          <Button icon={Play} onClick={() => navigate(practiceSongHref(ev.id, 0, first))}>
            {t('practice.open_first')}
          </Button>
        ) : null}
        {total > HOME_SONGS ? (
          <Button variant="secondary" onClick={() => setOpen(true)}>
            {t('practice.songs_title')} ({total})
          </Button>
        ) : null}
      </div>
      <PracticeSheet ev={ev} open={open} onClose={() => setOpen(false)} />
    </Card>
  );
}
